/**
 * VoiceOrb — Animated Canvas AI Voice Orb
 * =========================================
 * A living, breathing orb that responds to voice state and audio amplitude.
 * Rendered on <canvas> for 60fps performance — no DOM thrashing.
 *
 * Props:
 *   state      — 'idle' | 'listening' | 'processing' | 'speaking' | 'guardian'
 *   audioLevel — 0..1 (from Web Audio API RMS analysis)
 *   size       — canvas pixel size (default 220)
 *   onClick    — tap handler (start/stop recording)
 */

import { useEffect, useRef } from 'react';

const STATES = {
  idle: {
    c1: '#4facfe', c2: '#00f2fe', c3: '#006aff',
    speed: 0.3, pulse: 0.015, radius: 58,
  },
  listening: {
    c1: '#43e97b', c2: '#38f9d7', c3: '#00c851',
    speed: 0.8, pulse: 0.04, radius: 62,
  },
  processing: {
    c1: '#a18cd1', c2: '#fbc2eb', c3: '#7c4dff',
    speed: 2.0, pulse: 0.02, radius: 58,
  },
  speaking: {
    c1: '#f093fb', c2: '#f5576c', c3: '#ff4081',
    speed: 1.2, pulse: 0.06, radius: 64,
  },
  guardian: {
    c1: '#4facfe', c2: '#a8edea', c3: '#00bcd4',
    speed: 0.25, pulse: 0.008, radius: 56,
  },
};

const N = 8; // blob control points

function hexToRgb(h) {
  return [
    parseInt(h.slice(1, 3), 16),
    parseInt(h.slice(3, 5), 16),
    parseInt(h.slice(5, 7), 16),
  ];
}

function lerp(a, b, t) { return a + (b - a) * t; }

function noise(x) {
  return Math.sin(x * 1.3) * 0.5
       + Math.sin(x * 2.7 + 1) * 0.3
       + Math.sin(x * 5.1 + 2) * 0.2;
}

export default function VoiceOrb({ state = 'idle', audioLevel = 0, size = 220, onClick }) {
  const canvasRef = useRef(null);
  const stateRef  = useRef(STATES[state] || STATES.idle);
  const audioRef  = useRef(0);
  const animRef   = useRef(null);

  // Sync props to refs (avoids re-creating the animation loop)
  useEffect(() => {
    stateRef.current = STATES[state] || STATES.idle;
  }, [state]);

  useEffect(() => {
    audioRef.current = audioLevel;
  }, [audioLevel]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const cx = size / 2, cy = size / 2;

    const pts = Array.from({ length: N }, (_, i) => ({
      angle: (i / N) * Math.PI * 2,
      noise: Math.random() * 100,
      speed: 0.4 + Math.random() * 0.4,
    }));

    let curC1     = hexToRgb(STATES.idle.c1);
    let curC2     = hexToRgb(STATES.idle.c2);
    let curC3     = hexToRgb(STATES.idle.c3);
    let curRadius = STATES.idle.radius;
    let curSpeed  = STATES.idle.speed;
    let curPulse  = STATES.idle.pulse;
    let curAudio  = 0;
    let t = 0;

    function draw() {
      const s = stateRef.current;
      const targetAudio = audioRef.current;
      ctx.clearRect(0, 0, size, size);

      // Smooth interpolation toward target state
      const sp = 0.04;
      curC1     = curC1.map((v, i) => lerp(v, hexToRgb(s.c1)[i], sp));
      curC2     = curC2.map((v, i) => lerp(v, hexToRgb(s.c2)[i], sp));
      curC3     = curC3.map((v, i) => lerp(v, hexToRgb(s.c3)[i], sp));
      curRadius = lerp(curRadius, s.radius, 0.05);
      curSpeed  = lerp(curSpeed,  s.speed,  0.03);
      curPulse  = lerp(curPulse,  s.pulse,  0.05);
      curAudio  = lerp(curAudio, targetAudio, 0.12);

      const r2rgb = (c) => `rgb(${c.map(Math.round).join(',')})`;

      // ── Glow rings ──
      for (let ring = 3; ring >= 1; ring--) {
        const rSize = curRadius + ring * 18 + curAudio * 12;
        const alpha = (0.12 - ring * 0.03) * (1 + curAudio * 0.5);
        ctx.beginPath();
        ctx.arc(cx, cy, rSize, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${curC1.map(Math.round).join(',')},${alpha.toFixed(3)})`;
        ctx.fill();
      }

      // ── Organic blob ──
      const baseR = curRadius + curAudio * 14;
      const blobPts = pts.map((p) => {
        const n = noise(p.noise + t * p.speed * curSpeed);
        const wobble = n * (curRadius * 0.22 + curAudio * 10) * curPulse * 60;
        return {
          x: cx + Math.cos(p.angle + t * 0.1) * (baseR + wobble),
          y: cy + Math.sin(p.angle + t * 0.1) * (baseR + wobble),
        };
      });

      ctx.beginPath();
      for (let i = 0; i < N; i++) {
        const cur  = blobPts[i];
        const next = blobPts[(i + 1) % N];
        const mid  = { x: (cur.x + next.x) / 2, y: (cur.y + next.y) / 2 };
        if (i === 0) ctx.moveTo(mid.x, mid.y);
        else ctx.quadraticCurveTo(cur.x, cur.y, mid.x, mid.y);
      }
      const f = blobPts[0], last = blobPts[N - 1];
      ctx.quadraticCurveTo(last.x, last.y, (last.x + f.x) / 2, (last.y + f.y) / 2);
      ctx.closePath();

      // Radial gradient fill
      const grad = ctx.createRadialGradient(
        cx - curRadius * 0.25, cy - curRadius * 0.3, curRadius * 0.05,
        cx, cy, curRadius * 1.1
      );
      grad.addColorStop(0,   r2rgb(curC2));
      grad.addColorStop(0.5, r2rgb(curC1));
      grad.addColorStop(1,   r2rgb(curC3));
      ctx.fillStyle = grad;
      ctx.fill();

      // 3D highlight (top-left specular)
      const hl = ctx.createRadialGradient(
        cx - curRadius * 0.3, cy - curRadius * 0.35, curRadius * 0.02,
        cx - curRadius * 0.15, cy - curRadius * 0.1, curRadius * 0.7
      );
      hl.addColorStop(0,    'rgba(255,255,255,0.55)');
      hl.addColorStop(0.35, 'rgba(255,255,255,0.12)');
      hl.addColorStop(1,    'rgba(255,255,255,0)');
      ctx.fillStyle = hl;
      ctx.fill();

      // Bottom shadow
      const sh = ctx.createRadialGradient(
        cx + curRadius * 0.15, cy + curRadius * 0.3, 0,
        cx, cy, curRadius * 1.1
      );
      sh.addColorStop(0,   'rgba(0,0,0,0.15)');
      sh.addColorStop(0.5, 'rgba(0,0,0,0.06)');
      sh.addColorStop(1,   'rgba(0,0,0,0)');
      ctx.fillStyle = sh;
      ctx.fill();

      t += 0.016;
      animRef.current = requestAnimationFrame(draw);
    }

    draw();
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [size]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      onClick={onClick}
      data-testid="voice-orb-canvas"
      style={{ display: 'block', cursor: 'pointer', touchAction: 'none' }}
    />
  );
}
